import { fetchRecipesByIdMap } from "../recipes/recipeCatalog";
import { recipeCatalogToMealDetail } from "./recipeCatalogToMealDetail";
import { getMealFoodId, getRecipeDetails } from "./recipeId";

const cache = new Map();

export function seedRecipeDetails(rows = []) {
  rows.forEach((row) => {
    const foodId = getMealFoodId(row);
    const detail = getRecipeDetails(row);
    if (foodId && detail) {
      cache.set(String(foodId), { ...detail, food_id: foodId });
    }
  });
}

/** Returns cached details only; plans/templates embed recipe_details from Diet API. */
export async function fetchMissingRecipeDetails(foodIds = []) {
  const ids = [...new Set(foodIds.filter(Boolean).map(String))];
  return ids.map((id) => cache.get(id) || null);
}

/** Cache hit first; all remaining ids fetched in ONE batched request. */
export async function hydrateMissingRecipeDetails(foodIds = [], eventTypeId) {
  const ids = [...new Set(foodIds.filter(Boolean).map(String))];
  const map = {};
  const missing = [];

  ids.forEach((id) => {
    const cached = cache.get(id);
    if (cached) map[id] = cached;
    else missing.push(id);
  });
  if (!eventTypeId || missing.length === 0) return map;

  try {
    const res = await fetchRecipesByIdMap(eventTypeId, missing);
    if (!res?.success) return map;

    missing.forEach((key) => {
      const detail = recipeCatalogToMealDetail(res.recipesById[key]);
      if (detail) {
        const row = { ...detail, food_id: key };
        cache.set(key, row);
        map[key] = row;
      }
    });
  } catch {
    // Leave unresolved; caller falls back to id placeholder.
  }

  return map;
}

/**
 * Seed embedded details, then resolve every row's detail. With an eventTypeId,
 * all still-missing ids are fetched in ONE batched request; without it,
 * resolution is cache-only.
 */
export async function ensureRecipeDetailsForRows(rows = [], eventTypeId = null) {
  seedRecipeDetails(rows);
  const ids = rows.map((row) => getMealFoodId(row)).filter(Boolean);
  if (eventTypeId) {
    const hydrated = await hydrateMissingRecipeDetails(ids, eventTypeId);
    return new Map(ids.map((id) => [String(id), hydrated[String(id)] ?? null]));
  }
  const details = await fetchMissingRecipeDetails(ids);
  return new Map(ids.map((id, index) => [String(id), details[index]]));
}

export function cacheRecipeDetail(recipeId, detail) {
  if (recipeId && detail) {
    cache.set(String(recipeId), { ...detail, food_id: recipeId });
  }
}

export function clearRecipeDetailsCache() {
  cache.clear();
}
